import React, { ReactElement, useMemo } from 'react'

import ConnectionBreakdown, {
  ConnectionBreakdownData,
  ConnectionStationsProps,
} from '@components/TripBreakdown/ConnectionBreakdown'
import { useTripTypes } from '@hooks/useTripTypes'
import carrierUtils from '@lib/carrier'
import fareClassUtils from '@lib/fareClass'
import { useTranslation } from '@lib/i18n'
import openReturnUtils from '@lib/openReturn'
import weeklyUtils from '@lib/weekly'
import useTimetable from '@pages/Checkout/hooks/useTimetable'
import useValidity from '@pages/Checkout/hooks/useValidity'
import { useParams } from '@stores/params'

import '@components/TripBreakdown/index.scss'

interface TripBreakdownProps {
  express?: boolean
  timetable?: boolean
  outbound: ConnectionBreakdownData | null
  inbound: ConnectionBreakdownData | null
  carriers?: MarketingCarrier[]
  isCarriersLoading: boolean
  connections?: SortedConnections | null
  weeklyConnections?: WeeklyConnectionsParams
  fareClass?: ConnectionStationsProps['fareClass']
  /**
   * @default true
   */
  showFareFeatures?: ConnectionStationsProps['showFareFeatures']
}

const TripBreakdown = (props: TripBreakdownProps): ReactElement => {
  const {
    express,
    outbound,
    inbound,
    carriers = [],
    isCarriersLoading,
    fareClass,
    showFareFeatures = true,
    weeklyConnections,
    timetable,
    connections,
  } = props
  const { t } = useTranslation()
  const [params] = useParams()
  const isTwoWayTrip = !!inbound
  const weeklyTripsCount = weeklyUtils.getConnectionsCount(weeklyConnections)
  const weeklyTripsDates = weeklyUtils.getDatesRange(weeklyConnections)

  const resolvedFareClass: FareClass | undefined = useMemo(
    () => (fareClass ? fareClassUtils.getFareClassByCode(fareClass, carriers) : undefined),
    [fareClass, carriers],
  )

  const { isOpenReturnTrip, isLimitedOneTimeTrip, isDailyPassTrip } = useTripTypes(resolvedFareClass)

  const targetInbound = isOpenReturnTrip ? outbound && openReturnUtils.prepareOpenReturnInbound(outbound) : inbound

  const { outboundValidityMessage, inboundValidityMessage, getValidityRules } = useValidity({
    fareClass,
    carriers,
    inbound: targetInbound,
    outbound,
  })

  const list = useTimetable({ connections, enabled: timetable })

  const getTitle = (): string | null => {
    if (weeklyTripsCount) return t('checkout.bookingDetails.weeklyTrip', { count: weeklyTripsCount })
    if (isTwoWayTrip) return t('checkout.bookingDetails.outboundTrip')
    if (isOpenReturnTrip) return t('checkout.bookingDetails.outboundTrip')

    return null
  }

  const endOfValidityDate = useMemo(() => {
    const ticketValidityRules = fareClass ? getValidityRules(fareClass, 'inbound') : []

    if (ticketValidityRules.length === 0 || !targetInbound) return undefined

    return isOpenReturnTrip && targetInbound
      ? t('checkout.tripDetails.validUntil', {
          endDate: openReturnUtils.getEndOfValidity(targetInbound.departureTime, ticketValidityRules[0]),
        })
      : undefined
  }, [fareClass, getValidityRules, isOpenReturnTrip, targetInbound, t])

  const filterCarriers = (connection: ConnectionBreakdownData): MarketingCarrier[] => {
    return connection.segments.length > 0
      ? (carrierUtils.getCarriers(connection, carriers) as MarketingCarrier[])
      : carriers
  }

  const connectionBreakdownProps = {
    showTime: isDailyPassTrip
      ? false
      : !express && !weeklyTripsCount && !isOpenReturnTrip && !isLimitedOneTimeTrip && !isDailyPassTrip,
    showCarrier: !express && !!carriers.length,
    showDuration: express || isLimitedOneTimeTrip || isOpenReturnTrip || isDailyPassTrip,
    hideLocations: params.hideLocations,
  }

  return (
    <div className="trip-breakdown" data-tag="trip-breakdown">
      {isDailyPassTrip && <h4>{t('checkout.bookingDetails.dayPass')}</h4>}
      {outbound && (
        <ConnectionBreakdown
          {...connectionBreakdownProps}
          type="outbound"
          connection={outbound}
          carriers={filterCarriers(outbound)}
          title={getTitle()}
          fareClass={fareClass}
          showFareFeatures={showFareFeatures}
          validity={outboundValidityMessage}
          isLoading={isCarriersLoading}
          dates={weeklyTripsDates}
          timetable={{ list, connections: list?.outbounds }}
        />
      )}
      {targetInbound && !weeklyTripsCount && (
        <>
          <hr className="checkout__section-divider" />
          <ConnectionBreakdown
            {...connectionBreakdownProps}
            type="inbound"
            connection={targetInbound}
            carriers={filterCarriers(targetInbound)}
            title={
              isOpenReturnTrip ? t('checkout.bookingDetails.openReturnTrip') : t('checkout.bookingDetails.returnTrip')
            }
            dates={endOfValidityDate}
            fareClass={fareClass}
            showFareFeatures={showFareFeatures}
            validity={inboundValidityMessage}
            isLoading={isCarriersLoading}
            timetable={{ list, connections: list?.inbounds }}
          />
        </>
      )}
    </div>
  )
}

export default TripBreakdown
